type TrieNodeChildren = {
    [key: string]: TrieNode;
};

class TrieNode {
    char: string;
    parent: TrieNode | undefined;
    children: TrieNodeChildren;
    isEndOfWord: boolean;
    value: string | undefined;
    xrefs: Array<TrieNode>;

    constructor(char: string, parent?: TrieNode) {
        this.char = char;
        this.parent = parent;
        this.children = {};
        this.isEndOfWord = false;
        this.xrefs = [];
    }
}

export class Trie {
    root: TrieNode;

    constructor() {
        this.root = new TrieNode('');
    }

    insert(word: string, value?: string, xref?: TrieNode) {
        let node = this.root;
        for (let i = 0; i < word.length; i++) {
            const char = word[i];
            if (node.children[char] === undefined) {
                node.children[char] = new TrieNode(char, node);
            }
            node = node.children[char];
            if (xref) {
                node.xrefs.push(xref);
            }
        }
        node.isEndOfWord = !xref;
        node.value = value;
        return node;
    }

    /**
     * This function returns the subtree for an exact input prefix
     */
    private findNodeForPrefix(prefix: string): TrieNode | undefined {
        let node = this.root;
        for (let i = 0; i < prefix.length; i++) {
            const char = prefix[i];
            if (node.children[char]) {
                node = node.children[char];
            } else {
                return undefined;
            }
        }
        return node;
    }

    /**
     * This func returns subtrees for variations of the input prefix.
     * Variations: lowercase, uppercase
     * @param prefix to search for in the Trie
     */
    private findNodesForPrefix(prefix: string): Array<TrieNode> {
        const startNode = this.root;
        const resultNodes: Array<TrieNode> = [];
        this.traverseTreeWithCharVariations(startNode, prefix, resultNodes);
        return resultNodes;
    }

    private traverseTreeWithCharVariations(currNode: TrieNode, prefix: string, resultNodes: Array<TrieNode>) {
        if (prefix.length === 0) {
            resultNodes.push(currNode);
            return;
        }
        function buildCharVariations(char: string) {
            return [char.toLowerCase(), char.toUpperCase()];
        }
        buildCharVariations(prefix[0]).forEach((char: string) => {
            if (currNode.children[char]) {
                const remainingPrefix = prefix.slice(1);
                this.traverseTreeWithCharVariations(currNode.children[char], remainingPrefix, resultNodes);
            }
        });
    }

    private reconstructPrefix(node: TrieNode): string {
        let prefix = node.char;
        // traverse up
        let currNode = node.parent;
        while (currNode) {
            prefix += currNode.char;
            currNode = currNode.parent;
        }
        prefix = prefix.split('').reverse().join('');
        return prefix;
    }

    private searchSubtree(node: TrieNode, userInput: string, prefix: string, suggestions: Array<SearchResult>) {
        if (node.isEndOfWord) {
            suggestions.push({ word: userInput + prefix, value: node.value as string });
        }
        for (const [char, child] of Object.entries(node.children)) {
            this.searchSubtree(child, userInput, prefix + char, suggestions);
        }
    }

    private deduplicate(results: Array<SearchResult>): Array<SearchResult> {
        const values = new Set();
        return results.filter((res) => {
            const isDuplicate = values.has(res.value);
            values.add(res.value);
            return !isDuplicate;
        });
    }

    search(userInput: string): Array<SearchResult> {
        const suggestions: Array<SearchResult> = [];
        const subtrees = this.findNodesForPrefix(userInput);
        subtrees.forEach((subtree) => {
            if (subtree.isEndOfWord) {
                suggestions.push({ word: userInput, value: subtree.value as string });
            }
            // Recursively look for possible next characters
            this.searchSubtree(subtree, userInput, '', suggestions);

            // Add matches including, but not starting with this word
            subtree.xrefs.forEach((xrefNode) => {
                if (xrefNode.value) {
                    const searchResult = {
                        value: xrefNode.value as string,
                        word: this.reconstructPrefix(xrefNode),
                    };
                    suggestions.push(searchResult);
                } else {
                    console.error('Trie xref without value should not exist', xrefNode);
                }
            });
        });
        const deduped = this.deduplicate(suggestions);
        return deduped;
    }

    tokenize(input: string): Array<string> {
        return [...input.split(' - '), ...input.split(' ')];
    }
}

type SearchResult = {
    word: string;
    value: string;
};

type TrieInput = {
    word: string;
    value: string;
};

export function buildTrie(inputs: Array<TrieInput>) {
    const trie = new Trie();
    inputs.forEach((input) => {
        const mainNode = trie.insert(input.word, input.value);
        const relatedToken = trie.tokenize(input.word);
        relatedToken.forEach((token) => {
            // This token is already inserted and would overwrite the original node!
            if (token !== input.word) {
                trie.insert(token, undefined, mainNode);
            }
        });
    });
    return trie;
}
